"use client";
import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import type { MarketplacePackage } from "../../api/marketplaceApi";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { PublisherCard } from "./PublisherCard";
import { ComponentSelectionDialog } from "./ComponentSelectionDialog";

const emptyComponents = { skills: [] as string[], scripts: [] as string[], commands: [] as string[], mcp_servers: [] as string[] };

export interface MarketplaceRowProps {
  pkg: MarketplacePackage;
  onInstall: (
    name: string,
    config: { components: Record<string, string[]> },
    scope?: "company" | "user",
  ) => void;
  onUninstall: (name: string) => void;
  onUpdateConfig: (name: string, components: Record<string, string[]>) => void;
  isAdmin: boolean;
  busy?: boolean;
  /** Live connector state package (connector-type plugins only). */
  connectorState?: "connected" | "needs_auth" | "disabled";
  onConnect?: (slug: string) => void;
  onDisconnect?: (slug: string) => void;
  /** Install this package AGAIN alongside the existing install — a second
   *  Jira board. Only offered when the package declares `multi_install`. */
  onInstallAnother?: (name: string, label: string) => void;
}

/** One package in the marketplace list — display_name + description,
 *  ring badge, publisher chip, and an action affordance:
 *  - not installed → Install button (opens component selection dialog if the
 *    package has components, otherwise installs directly).
 *  - installed → "Installed" chip + Uninstall button + (if admin + components)
 *    an "Edit components" button.
 *  The action is disabled while a request is in flight (``busy``) or when
 *  the viewer is not an admin; non-admins get a "Requires an admin" tooltip
 *  (rendered via a <span> wrapper so it shows on the disabled button). */
export function MarketplaceRow({
  pkg,
  onInstall,
  onUninstall,
  onUpdateConfig,
  isAdmin,
  busy,
  connectorState,
  onConnect,
  onDisconnect,
  onInstallAnother,
}: MarketplaceRowProps) {
  const actionDisabled = busy || !isAdmin;
  const tip = !isAdmin ? "Requires an admin" : "";
  // Installing FOR THE TENANT needs admin; installing for yourself does not
  // (the backend gates the same way). So a non-admin installs personally
  // rather than facing a disabled button — and an admin CHOOSES, because
  // wanting a connector for yourself alone is not an admin-only wish.
  const installDisabled = busy;
  const installTip = isAdmin ? "" : "Installs for you only";
  const isConnector = pkg.category === "connector";
  const [dialog, setDialog] = useState<null | "install" | "edit" | "another">(
    null,
  );
  const [anotherLabel, setAnotherLabel] = useState("");
  // An admin may install for the whole company or just for themselves; a
  // non-admin can only do the latter, so the choice is not offered to them.
  const [scope, setScope] = useState<"company" | "user">("company");
  const installScope: "company" | "user" = isAdmin ? scope : "user";

  const components = pkg.components ?? emptyComponents;
  const hasComponents =
    components.skills.length > 0 ||
    components.scripts.length > 0 ||
    components.commands.length > 0 ||
    components.mcp_servers.length > 0;

  function handleInstallClick() {
    if (hasComponents) {
      setDialog("install");
    } else {
      onInstall(pkg.name, { components: {} }, installScope);
    }
  }

  function handleDialogConfirm(mask: Record<string, string[]>) {
    setDialog(null);
    if (dialog === "install") {
      onInstall(pkg.name, { components: mask }, installScope);
    } else {
      onUpdateConfig(pkg.name, mask);
    }
  }

  function handleAnotherConfirm() {
    const label = anotherLabel.trim();
    setDialog(null);
    setAnotherLabel("");
    // The label is what tells two boards apart in the list, so require it
    // rather than creating a second install called the same as the first.
    if (label) onInstallAnother?.(pkg.name, label);
  }

  return (
    <WorkspaceCard
      interactive={false}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        ...(busy ? { pointerEvents: "none", opacity: 0.6 } : null),
      }}
      aria-label={`marketplace-package ${pkg.name}`}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle1">{pkg.display_name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {pkg.description}
        </Typography>
      </Box>
      <Chip
        label={`Ring ${pkg.ring}`}
        size="small"
        aria-label={`ring-${pkg.ring}`}
      />
      <PublisherCard publisher={pkg.publisher} />
      {isConnector && (
        <Chip
          label="Connector"
          size="small"
          variant="outlined"
          aria-label={`connector-${pkg.name}`}
        />
      )}
      {pkg.installed ? (
        <>
          <Chip
            label={
              (pkg.instances?.length ?? 0) > 1
                ? `Installed (${pkg.instances!.length})`
                : "Installed"
            }
            color="success"
            size="small"
            aria-label={`installed-${pkg.name}`}
          />
          {/* One chip per install once there are several, so two Jira boards
              are distinguishable. A single install stays as it was. */}
          {(pkg.instances?.length ?? 0) > 1 &&
            pkg.instances!.map((inst) => (
              <Chip
                key={inst.install_id}
                label={inst.label || inst.install_id}
                size="small"
                variant="outlined"
                aria-label={`instance-${inst.install_id}`}
              />
            ))}
          {pkg.multi_install && isAdmin && onInstallAnother && (
            <Tooltip title={tip}>
              <span>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setDialog("another")}
                  disabled={actionDisabled}
                  aria-label={`add-another-${pkg.name}`}
                >
                  Add another
                </Button>
              </span>
            </Tooltip>
          )}
          {isAdmin && hasComponents && (
            <Tooltip title={tip}>
              <span>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setDialog("edit")}
                  disabled={actionDisabled}
                >
                  Edit components
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title={tip}>
            <span>
              <Button
                size="small"
                color="inherit"
                onClick={() => onUninstall(pkg.name)}
                disabled={actionDisabled}
                aria-label={`uninstall-${pkg.name}`}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
              >
                Uninstall
              </Button>
            </span>
          </Tooltip>
          {isConnector && connectorState === "connected" && (
            <Button
              size="small"
              color="success"
              onClick={() => onDisconnect?.(pkg.name)}
              disabled={busy}
              aria-label={`disconnect-connector-${pkg.name}`}
            >
              Connected ✓
            </Button>
          )}
          {isConnector && connectorState !== "connected" && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => onConnect?.(pkg.name)}
              disabled={busy}
              aria-label={`connect-${pkg.name}`}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Connect
            </Button>
          )}
        </>
      ) : (
        <>
          {/* Who the install is for. Only an admin has a choice — a
              non-admin can only install for themselves, so a disabled
              toggle would be noise. */}
          {isAdmin && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={scope}
              onChange={(_e, next) => {
                if (next) setScope(next as "company" | "user");
              }}
              aria-label={`scope-${pkg.name}`}
              disabled={busy}
            >
              <ToggleButton value="company" aria-label={`scope-company-${pkg.name}`}>
                Company
              </ToggleButton>
              <ToggleButton value="user" aria-label={`scope-personal-${pkg.name}`}>
                Personal
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Tooltip title={installTip}>
          <span>
            <Button
              size="small"
              variant="contained"
              onClick={handleInstallClick}
              disabled={installDisabled}
              aria-label={`install-${pkg.name}`}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Install
            </Button>
          </span>
          </Tooltip>
        </>
      )}
      <ComponentSelectionDialog
        open={dialog === "install" || dialog === "edit"}
        components={components}
        current={dialog === "edit" ? pkg.selected : undefined}
        confirmLabel={dialog === "edit" ? "Save" : "Install"}
        onClose={() => setDialog(null)}
        onConfirm={handleDialogConfirm}
      />
      {/* A second install needs a name to tell it apart in the list; the
          component selection is inherited and editable afterwards. */}
      <Dialog
        open={dialog === "another"}
        onClose={() => setDialog(null)}
        aria-label={`add-another-dialog-${pkg.name}`}
      >
        <DialogTitle>Add another {pkg.display_name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name this one"
            placeholder="Jira — EU board"
            value={anotherLabel}
            onChange={(e) => setAnotherLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && anotherLabel.trim()) handleAnotherConfirm();
            }}
            slotProps={{
              htmlInput: { "aria-label": `another-label-${pkg.name}` },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            onClick={handleAnotherConfirm}
            disabled={!anotherLabel.trim()}
            aria-label={`confirm-another-${pkg.name}`}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </WorkspaceCard>
  );
}
